/**
 * TargetScopePicker — choose « Toutes les cibles » or ONE Cibles target for a Brand
 * Knowledge list item (`BrandStrategy.itemTargets`).
 *
 * The trigger is either a quiet pill (`anchor="chip"`, one per list row) or a small
 * button (`anchor="button"`, the bulk « Assigner à… » action). The popover is a search
 * field over a keyboard-navigable listbox grouped by category.
 *
 * A value that does not resolve renders as « Toutes les cibles », which is how every
 * reader of `itemTargets` treats it. When the id is CONFIRMED deleted (`missingIds`) the
 * pill carries an amber ring and says so; an id that is merely unresolved (lookup failed)
 * gets no such claim.
 */
import { memo, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, InputBase, Popover, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { TargetCategory } from '@traaker/shared';
import { CAT_BY_ID } from '../../../pages/targets/CATEGORIES';
import { targetColorHex } from '../../../utils/targetColors';
import { foldText } from './scopedList';
import { Highlight } from './Highlight';
import { K, markSx, microLabel } from './tokens';
import {
  PICKABLE_CATEGORIES,
  PICKER_MAX_PER_CATEGORY,
  type StrategyTargetOption,
  type StrategyTargets,
} from './useStrategyTargets';

export interface TargetScopePickerProps {
  value?: string;
  onChange: (targetId: string | undefined) => void;
  targets: StrategyTargets;
  disabled?: boolean;
  anchor?: 'chip' | 'button';
  /** `anchor="button"` only: the button text (default « Assigner à… »). */
  label?: string;
}

/** Options rendered per category before « affinez la recherche » — keeps the popover light. */
const MAX_SHOWN_PER_GROUP = 60;

type Entry = { kind: 'all' } | { kind: 'target'; option: StrategyTargetOption };

function Swatch({ color, ring }: { color?: string; ring?: boolean }) {
  return (
    <Box
      component="i"
      sx={{
        width: 7, height: 7, borderRadius: '999px', flex: '0 0 7px', display: 'block',
        ...(ring ? { boxShadow: `inset 0 0 0 1.6px ${K.warning}` } : { bgcolor: color }),
      }}
    />
  );
}

function TargetScopePickerImpl({ value, onChange, targets, disabled, anchor = 'chip', label }: TargetScopePickerProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const uid = useId();

  const resolved = value ? targets.resolve(value) : undefined;
  const deleted = !!value && targets.missingIds.has(value);
  const pending = !!value && !resolved && !deleted && targets.loading;

  const marqueLabel = useMemo(
    () => new Map(targets.options.filter((o) => o.category === 'marque').map((o) => [o.targetId, o.label])),
    [targets.options],
  );
  const showParent = marqueLabel.size > 1;

  // Grouped + filtered — computed only while open.
  const { groups, flat } = useMemo(() => {
    if (!open) return { groups: [] as Array<{ cat: TargetCategory; shown: StrategyTargetOption[]; hidden: number; total: number }>, flat: [] as Entry[] };
    const q = foldText(query.trim());
    const flatList: Entry[] = [];
    if (!q || foldText(t('brandKnowledge.scope.all')).includes(q)) flatList.push({ kind: 'all' });
    const out = PICKABLE_CATEGORIES.map((cat) => {
      const all = targets.options
        .filter((o) => o.category === cat && (!q || foldText(o.label).includes(q)))
        .sort((a, b) => a.label.localeCompare(b.label));
      const shown = all.slice(0, MAX_SHOWN_PER_GROUP);
      for (const option of shown) flatList.push({ kind: 'target', option });
      return { cat, shown, hidden: all.length - shown.length, total: all.length };
    }).filter((g) => g.total > 0);
    return { groups: out, flat: flatList };
  }, [open, query, targets.options, t]);

  // Land the cursor on the current value when opening, on the first hit when typing.
  useEffect(() => {
    if (!open) return;
    const idx = flat.findIndex((e) => (e.kind === 'all' ? !resolved : e.option.targetId === resolved?.targetId));
    setActive(query ? 0 : Math.max(0, idx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = () => { setOpen(false); setQuery(''); };
  const pick = (e: Entry) => {
    onChange(e.kind === 'all' ? undefined : e.option.targetId);
    close();
  };

  const onKeyDown = (ev: KeyboardEvent) => {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (ev.key === 'Home') { ev.preventDefault(); setActive(0); }
    else if (ev.key === 'End') { ev.preventDefault(); setActive(Math.max(0, flat.length - 1)); }
    else if (ev.key === 'Enter') { ev.preventDefault(); const e = flat[active]; if (e) pick(e); }
  };

  // ── Trigger ──
  const allLabel = t('brandKnowledge.scope.all');
  let trigger: ReactElement;
  if (anchor === 'button') {
    trigger = (
      <ButtonBase
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        sx={{
          gap: 0.5, border: `1px solid ${K.line}`, bgcolor: K.paper, borderRadius: '8px',
          px: 1.1, py: 0.5, fontSize: 12, fontWeight: 600, color: K.ink,
          '&:hover': { bgcolor: K.primarySoft },
          '&.Mui-disabled': { color: K.ink3 },
        }}
      >
        {label ?? t('brandKnowledge.list.bulkAssign')}
        <KeyboardArrowDownIcon sx={{ fontSize: 15, color: K.ink3 }} />
      </ButtonBase>
    );
  } else {
    const color = resolved ? targetColorHex(resolved.color) : undefined;
    const text = pending ? t('brandKnowledge.scope.loading') : resolved ? resolved.label : allLabel;
    const pill = (
      <ButtonBase
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-label={`${t('brandKnowledge.scope.pickerAria')} : ${text}${deleted ? ` (${t('brandKnowledge.scope.deleted')})` : ''}`}
        sx={{
          maxWidth: '100%', minWidth: 0, gap: 0.75, justifyContent: 'flex-start',
          borderRadius: '999px', px: 1, py: '3px', fontSize: 11.5, fontWeight: 600, lineHeight: '16px',
          whiteSpace: 'nowrap',
          border: '1px solid transparent',
          ...(resolved
            ? { bgcolor: K.chipBg, color: K.ink }
            : { bgcolor: 'transparent', color: K.ink3, fontWeight: 500 }),
          '&:hover': { bgcolor: K.primarySoft, borderColor: 'rgba(124,58,237,.22)', color: K.primaryDark },
          '&.Mui-disabled': { color: resolved ? K.ink : K.ink3 },
          '&.Mui-focusVisible': { boxShadow: K.focusRing },
        }}
      >
        {resolved ? <Swatch color={color} /> : deleted ? <Swatch ring /> : null}
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{text}</Box>
        {!disabled && <KeyboardArrowDownIcon sx={{ fontSize: 14, color: K.ink3, ml: -0.25, flex: '0 0 auto' }} />}
      </ButtonBase>
    );
    trigger = deleted ? (
      <Tooltip title={t('brandKnowledge.scope.deletedHint')} arrow>
        <span style={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}>{pill}</span>
      </Tooltip>
    ) : resolved ? (
      <Tooltip title={resolved.label} enterDelay={600}>
        <span style={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}>{pill}</span>
      </Tooltip>
    ) : pill;
  }

  // ── Popover ──
  let idx = -1;
  const optionRow = (entry: Entry) => {
    idx += 1;
    const i = idx;
    const isAll = entry.kind === 'all';
    const selected = isAll ? !resolved : entry.option.targetId === resolved?.targetId;
    const o = isAll ? undefined : entry.option;
    const parent = o?.parentTargetId && showParent ? marqueLabel.get(o.parentTargetId) : undefined;
    return (
      <Box
        key={isAll ? '__all__' : o!.targetId}
        id={`${uid}-opt-${i}`}
        role="option"
        aria-selected={selected}
        data-idx={i}
        onMouseEnter={() => setActive(i)}
        onClick={() => pick(entry)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1, py: '5px', mx: 0.5, borderRadius: '6px',
          cursor: 'pointer', fontSize: 12.5, color: K.ink, minWidth: 0,
          bgcolor: i === active ? K.primarySoft : 'transparent',
          ...markSx,
        }}
      >
        {isAll ? <AllInclusiveIcon sx={{ fontSize: 14, color: K.ink2, flex: '0 0 14px' }} /> : <Swatch color={targetColorHex(o!.color)} />}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.75, overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, fontWeight: isAll ? 600 : 500 }}>
            <Highlight text={isAll ? allLabel : o!.label} query={query} />
          </Box>
          {parent && (
            <Box component="span" sx={{ fontSize: 10.5, color: K.ink3, flex: '0 0 auto' }}>{parent}</Box>
          )}
        </Box>
        {selected && <CheckIcon sx={{ fontSize: 15, color: K.primary, flex: '0 0 auto' }} />}
      </Box>
    );
  };

  const noTargets = !targets.loading && targets.options.length === 0;
  const allEntry = flat.find((e) => e.kind === 'all');

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: anchor === 'button' ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: anchor === 'button' ? 'left' : 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5, width: 320, borderRadius: '12px', border: `1px solid ${K.line}`,
              boxShadow: '0 1px 2px rgba(0,0,0,.03), 0 16px 40px rgba(0,0,0,.14)',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
            },
          },
        }}
      >
        <Box sx={{ p: 1, pb: 0.75, borderBottom: `1px solid ${K.line}` }}>
          <Box
            sx={{
              position: 'relative', display: 'flex', alignItems: 'center',
              border: `1px solid ${K.line}`, borderRadius: '10px', bgcolor: K.paper,
              '&:focus-within': { borderColor: K.primary, boxShadow: K.focusRing },
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: K.ink3, position: 'absolute', left: 8, pointerEvents: 'none' }} />
            <InputBase
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('brandKnowledge.scope.searchPlaceholder')}
              inputProps={{
                'aria-label': t('brandKnowledge.scope.searchPlaceholder'),
                role: 'combobox',
                'aria-expanded': true,
                'aria-controls': `${uid}-listbox`,
                'aria-activedescendant': flat.length ? `${uid}-opt-${active}` : undefined,
              }}
              sx={{ flex: 1, fontSize: 12.5, pl: '28px', pr: 1, py: '3px' }}
            />
          </Box>
        </Box>

        <Box ref={listRef} id={`${uid}-listbox`} role="listbox" sx={{ maxHeight: 340, overflowY: 'auto', overscrollBehavior: 'contain', py: 0.5 }}>
          {allEntry && optionRow(allEntry)}
          {groups.map((g) => {
            const cfg = CAT_BY_ID[g.cat];
            const Icon = cfg?.Icon;
            return (
              <Box key={g.cat} role="group" aria-label={cfg ? t(cfg.labelKey) : g.cat}>
                <Box
                  sx={{
                    ...microLabel, display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1.5, pt: 1.25, pb: 0.5, position: 'sticky', top: -4, bgcolor: K.paper, zIndex: 1,
                  }}
                >
                  {Icon && <Icon sx={{ fontSize: 13, color: cfg.color }} />}
                  {cfg ? t(cfg.labelKey) : g.cat}
                  <Box component="span" sx={{ ml: 'auto', fontWeight: 500, letterSpacing: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {g.total}
                  </Box>
                </Box>
                {g.shown.map((option) => optionRow({ kind: 'target', option }))}
                {g.hidden > 0 && (
                  <Box sx={{ px: 1.5, py: 0.5, fontSize: 11.5, color: K.ink3 }}>
                    {t('brandKnowledge.scope.moreHidden', { count: g.hidden })}
                  </Box>
                )}
              </Box>
            );
          })}
          {flat.length === 0 && !noTargets && (
            <Box sx={{ px: 1.5, py: 1.5, fontSize: 12.5, color: K.ink2 }}>{t('brandKnowledge.scope.none')}</Box>
          )}
          {targets.loading && targets.options.length === 0 && (
            <Box sx={{ px: 1.5, py: 1, fontSize: 12, color: K.ink3 }}>{t('brandKnowledge.scope.loading')}</Box>
          )}
        </Box>

        {(noTargets || targets.truncated || targets.error) && (
          <Box sx={{ borderTop: `1px solid ${K.line}`, px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {noTargets && !targets.error && (
              <Box sx={{ fontSize: 11.5, color: K.ink2, lineHeight: 1.45 }}>{t('brandKnowledge.scope.noTargets')}</Box>
            )}
            {targets.truncated && (
              <Box sx={{ fontSize: 11.5, color: K.warningInk, lineHeight: 1.45 }}>
                {t('brandKnowledge.scope.truncated', { n: PICKER_MAX_PER_CATEGORY })}
              </Box>
            )}
            {targets.error && (
              <Box sx={{ fontSize: 11.5, color: K.errorInk, lineHeight: 1.45 }}>{t('brandKnowledge.scope.loadError')}</Box>
            )}
          </Box>
        )}
      </Popover>
    </>
  );
}

export const TargetScopePicker = memo(TargetScopePickerImpl);
export default TargetScopePicker;
